<app-iota-modal title="INH Billing Process" i18n-title [modalOptions]="modalOptions" (close$)="closeDialog()">
    <div modal-body class="p-5 flex flex-col gap-3">
        <div class="flex flex-col gap-3">
            <span class="font-bold text-xl" i18n>Create INH invoices</span>
            <span i18n>
                Select one or more business partners and determine whether product or license invoices should be created. Currently only one
                invoice type can be processed at a time. Please note that after the creation process, all documents must first be approved
                or rejected before further invoicing can be started.
            </span>
        </div>
        <app-smart-dialog-steps [stepLabels]="stepLabels" [currentStep]="currentStep$ | ngrxPush"></app-smart-dialog-steps>

        <div class="w-full p-5 rounded-md border-2 border-iota-outline flex flex-col gap-5" *ngIf="isInSettingsStep$ | ngrxPush">
            <select class="w-full select select-bordered rounded-lg border-iota-outline" (change)="updateBusinessPartnerType($event)">
                <option disabled value="" [selected]="!(selectedBusinessPartnerType$ | ngrxPush)" i18n>
                    Select Business Partner type…
                </option>
                <option
                    *ngFor="let businessPartnerType of businessPartnerTypes"
                    [value]="businessPartnerType"
                    [selected]="businessPartnerType === (selectedBusinessPartnerType$ | ngrxPush)"
                >
                    {{ businessPartnerTypeLabels?.[businessPartnerType] }}
                </option>
            </select>
            <mat-select
                class="w-full select select-bordered rounded-lg border-iota-outline"
                [value]="selectedBusinessPartners$ | ngrxPush"
                multiple
                (selectionChange)="updateSelectedBusinessPartners($event)"
                [disabled]="selectBusinessPartnersDisabled$ | ngrxPush"
                [placeholder]="selectBusinessPartnersPlaceholder$ | ngrxPush"
            >
                <mat-select-trigger>
                    <span>{{ businessPartnersSelectLabel$ | ngrxPush }}</span>
                </mat-select-trigger>
                <mat-option (click)="clearSelectedBusinessPartners()" class="clear-selection font-bold mat-select-with-remove-all">
                    <span class="flex items-center text-lg pl-0.5">x</span>
                    <span i18n class="pt-1">Clear selection</span>
                </mat-option>
                <ng-container *ngIf="showCentralRegulatorsInBusinessPartnerSelection$ | ngrxPush">
                    <mat-option
                        *ngFor="let centralRegulator of centralRegulators$ | ngrxPush"
                        [value]="centralRegulator?.centralRegulatorId"
                    >
                        {{ centralRegulator?.name }}&nbsp;({{ centralRegulator?.centralRegulatorNumber }})
                    </mat-option>
                </ng-container>
                <ng-container *ngIf="showCustomersInBusinessPartnerSelection$ | ngrxPush">
                    <ng-container *ngFor="let company of companies$ | ngrxPush; let i = index; let companies = count">
                        <mat-option
                            *ngIf="i === getFirstDeactivatedIndex(companies$ | ngrxPush)"
                            disabled
                            class="!h-0 !min-h-0 !p-0 border-t border-iota-outline !cursor-default"
                        >
                        </mat-option>
                        <mat-option [value]="company?.companyId" [ngClass]="{ 'text-gray-400': isCompanyDeactivated(company) }">
                            <span [ngClass]="{ 'font-normal': !isCompanyDeactivated(company) }">{{ getCompanyName(company) }}</span>
                            <span *ngIf="isCompanyDeactivated(company)" class="font-bold" i18n> (deactivated)</span>
                        </mat-option>
                    </ng-container>
                </ng-container>
            </mat-select>
            <select class="w-full select select-bordered rounded-lg border-iota-outline" (change)="updateInvoicingType($event)">
                <option disabled value="" [selected]="!(selectedInvoicingType$ | ngrxPush)" i18n>Select invoice type…</option>
                <option
                    *ngFor="let invoicingType of invoicingTypes"
                    [value]="invoicingType"
                    [selected]="invoicingType === (selectedInvoicingType$ | ngrxPush)"
                >
                    {{ invoicingTypeLabels?.[invoicingType] }}
                </option>
            </select>
            <select class="w-full select select-bordered rounded-lg border-iota-outline" (change)="updateLocale($event)">
                <option disabled value="" [selected]="!(selectedLocale$ | ngrxPush)" i18n>Select document language…</option>
                <option *ngFor="let locale of invoicingLocales" [value]="locale" [selected]="locale === (selectedLocale$ | ngrxPush)">
                    {{ invoicingLocaleLabels?.[locale] }}
                </option>
            </select>
        </div>

        <div class="flex flex-col items-center justify-center gap-5" *ngIf="isInResultStep$ | ngrxPush">
            <div class="spinner spinner-primary spinner-xl" *ngIf="loading$ | ngrxPush"></div>

            <div class="flex flex-col items-center gap-5" *ngIf="!(loading$ | ngrxPush) && !(error$ | ngrxPush)">
                <iota-icon icon="check-circle" class="text-primary" size="7"></iota-icon>
                <h4 class="mb-0" i18n>Billing process completed!</h4>
                <button
                    class="btn btn-sm btn-primary text-sm px-5 rounded-full flex flex-row items-center gap-2"
                    (click)="downloadReport()"
                >
                    <span i18n>Download Processing Report</span>
                </button>
            </div>

            <div class="flex flex-col items-center gap-5" *ngIf="!(loading$ | ngrxPush) && (error$ | ngrxPush)">
                <iota-icon icon="x-circle" class="text-error" size="7"></iota-icon>
                <h4 class="mb-0" i18n>Error while running the billing process</h4>
                <span i18n>Please contact our support.</span>
                <div class="collapse collapse-arrow border">
                    <input type="checkbox" />
                    <div class="collapse-title">
                        <div class="flex flex-row items-center gap-5" i18n>Detailed error message</div>
                    </div>
                    <div class="collapse-content flex flex-col gap-1">
                        <pre class="overflow-x-auto">
                            {{ error$ | ngrxPush | json }}
                        </pre>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <ng-container modal-footer *ngIf="!(isInResultStep$ | ngrxPush)">
        <button class="btn btn-sm btn-ghost rounded-full" *ngIf="isInSettingsStep$ | ngrxPush" (click)="closeDialog()" i18n>Cancel</button>
        <div class="flex-1"></div>
        <button
            class="btn btn-sm btn-primary rounded-full flex flex-row items-center gap-2"
            [disabled]="!(dataValid$ | ngrxPush)"
            (click)="startInvoiceProcess()"
            i18n
        >
            Start billing process
        </button>
    </ng-container>

    <ng-container modal-footer *ngIf="isInResultStep$ | ngrxPush">
        <button class="btn btn-sm btn-primary rounded-full flex flex-row items-center gap-2" (click)="closeDialog()">
            <span i18n>Close</span>
        </button>
    </ng-container>
</app-iota-modal>
